import React, { useState } from 'react'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material'
import {
  Add as AddIcon,
  Autorenew as RotateIcon,
  Block as RevokeIcon,
  ContentCopy as CopyIcon
} from '@mui/icons-material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import apiKeyService, { type ApiKey, type ApiKeyCreated, type ScopeOption } from '../services/apiKeyService'

const PRESETS: Record<string, { label: string; name: string; scopes: string[] }> = {
  cart_recovery: {
    label: 'n8n: cart recovery reminders',
    name: 'n8n cart recovery',
    scopes: ['sales.carts.read', 'sales.carts.recover']
  },
  leads: {
    label: 'n8n: lead intake',
    name: 'n8n lead intake',
    scopes: ['leads.read', 'leads.write']
  },
  custom: { label: 'Custom scopes', name: '', scopes: [] }
}

const formatDate = (value: string | null) => (value ? new Date(value).toLocaleString() : '–')

const errorText = (e: unknown): string => {
  const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
  return typeof detail === 'string' ? detail : 'Request failed. Please try again.'
}

const ApiKeysPage: React.FC = () => {
  const queryClient = useQueryClient()
  const [createOpen, setCreateOpen] = useState(false)
  const [preset, setPreset] = useState('cart_recovery')
  const [name, setName] = useState(PRESETS.cart_recovery.name)
  const [scopes, setScopes] = useState<string[]>(PRESETS.cart_recovery.scopes)
  const [expiresInDays, setExpiresInDays] = useState<number | ''>(365)
  const [created, setCreated] = useState<ApiKeyCreated | null>(null)
  const [confirm, setConfirm] = useState<{ type: 'rotate' | 'revoke'; key: ApiKey } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const { data, isLoading } = useQuery({ queryKey: ['api-keys'], queryFn: apiKeyService.list })
  const { data: scopeData } = useQuery({ queryKey: ['api-key-scopes'], queryFn: apiKeyService.availableScopes })
  const scopeOptions: ScopeOption[] = scopeData?.scopes || []
  const scopeInfo = new Map(scopeOptions.map((s) => [s.scope, s]))

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['api-keys'] })

  const createMutation = useMutation({
    mutationFn: () =>
      apiKeyService.create({
        name: name.trim(),
        scopes,
        ...(expiresInDays ? { expires_in_days: Number(expiresInDays) } : {})
      }),
    onSuccess: (key) => {
      setCreateOpen(false)
      setCreated(key)
      setError(null)
      refresh()
    },
    onError: (e) => setError(errorText(e))
  })

  const confirmMutation = useMutation({
    mutationFn: async () => {
      if (!confirm) return null
      if (confirm.type === 'revoke') {
        await apiKeyService.revoke(confirm.key.id)
        return null
      }
      return apiKeyService.rotate(confirm.key.id)
    },
    onSuccess: (key) => {
      setConfirm(null)
      setError(null)
      if (key) setCreated(key)
      refresh()
    },
    onError: (e) => {
      setConfirm(null)
      setError(errorText(e))
    }
  })

  const openCreate = () => {
    setPreset('cart_recovery')
    setName(PRESETS.cart_recovery.name)
    setScopes(PRESETS.cart_recovery.scopes)
    setExpiresInDays(365)
    setError(null)
    setCreateOpen(true)
  }

  const choosePreset = (value: string) => {
    setPreset(value)
    if (value !== 'custom') {
      setName(PRESETS[value].name)
      setScopes(PRESETS[value].scopes)
    }
  }

  const copyKey = async () => {
    if (!created) return
    try {
      await navigator.clipboard.writeText(created.raw_key)
      setCopied(true)
    } catch {
      setCopied(false)
    }
  }

  const keys = data?.items || []

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h4" component="h1">
            API Keys
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Keys for n8n and other automations. Send as the <code>X-API-Key</code> header.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          Create API key
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          {isLoading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Key</TableCell>
                    <TableCell>Scopes</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Last used</TableCell>
                    <TableCell>Expires</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {keys.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography variant="body2" color="textSecondary" py={3}>
                          No API keys yet
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {keys.map((key) => (
                    <TableRow key={key.id} hover>
                      <TableCell>{key.name}</TableCell>
                      <TableCell>
                        <code>{key.key_prefix}</code>
                      </TableCell>
                      <TableCell>
                        {key.scopes.map((scope) => (
                          <Chip key={scope} label={scope} size="small" variant="outlined" sx={{ mr: 0.5, mb: 0.5 }} />
                        ))}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={key.is_active ? 'Active' : 'Revoked'}
                          size="small"
                          color={key.is_active ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell>{formatDate(key.last_used_at)}</TableCell>
                      <TableCell>{formatDate(key.expires_at)}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Rotate: issue a new secret, the old one stops working">
                          <span>
                            <IconButton
                              size="small"
                              disabled={!key.is_active}
                              onClick={() => setConfirm({ type: 'rotate', key })}
                            >
                              <RotateIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Revoke">
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              disabled={!key.is_active}
                              onClick={() => setConfirm({ type: 'revoke', key })}
                            >
                              <RevokeIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Create */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Create API key</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <FormControl fullWidth size="small">
              <InputLabel>Use for</InputLabel>
              <Select value={preset} label="Use for" onChange={(e) => choosePreset(e.target.value)}>
                {Object.entries(PRESETS).map(([id, p]) => (
                  <MenuItem key={id} value={id}>
                    {p.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField label="Name" size="small" value={name} onChange={(e) => setName(e.target.value)} fullWidth />
            <Autocomplete
              multiple
              size="small"
              options={scopeOptions.map((s) => s.scope)}
              groupBy={(option) => scopeInfo.get(option)?.group || 'other'}
              value={scopes}
              onChange={(_e, value) => {
                setScopes(value)
                setPreset('custom')
              }}
              filterSelectedOptions
              getOptionLabel={(option) => option}
              renderOption={(props, option) => (
                <li {...props} key={option}>
                  <Box>
                    <Typography variant="body2">{option}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {scopeInfo.get(option)?.description}
                    </Typography>
                  </Box>
                </li>
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip {...getTagProps({ index })} key={option} label={option} size="small" />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Scopes"
                  helperText={`Search ${scopeOptions.length || 'all'} permissions. Only grant what the automation needs.`}
                />
              )}
            />
            <FormControl fullWidth size="small">
              <InputLabel>Expires</InputLabel>
              <Select
                value={expiresInDays}
                label="Expires"
                onChange={(e) => setExpiresInDays(e.target.value as number | '')}
              >
                <MenuItem value={90}>In 90 days</MenuItem>
                <MenuItem value={180}>In 180 days</MenuItem>
                <MenuItem value={365}>In 1 year</MenuItem>
                <MenuItem value="">Never</MenuItem>
              </Select>
            </FormControl>
            {error && <Alert severity="error">{error}</Alert>}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={name.trim().length < 2 || scopes.length === 0 || createMutation.isPending}
            onClick={() => createMutation.mutate()}
          >
            {createMutation.isPending ? 'Creating…' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Secret shown once */}
      <Dialog open={!!created} onClose={() => undefined} fullWidth maxWidth="sm">
        <DialogTitle>Copy your API key now</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This is the only time the full key is shown. Store it in n8n as a Header Auth credential
            (header <code>X-API-Key</code>).
          </Alert>
          <Box display="flex" gap={1} alignItems="center">
            <TextField
              fullWidth
              size="small"
              value={created?.raw_key || ''}
              InputProps={{ readOnly: true, sx: { fontFamily: 'monospace' } }}
            />
            <Tooltip title={copied ? 'Copied' : 'Copy'}>
              <IconButton onClick={copyKey}>
                <CopyIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => {
              setCreated(null)
              setCopied(false)
            }}
          >
            I have saved it
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rotate / revoke */}
      <Dialog open={!!confirm} onClose={() => setConfirm(null)}>
        <DialogTitle>{confirm?.type === 'rotate' ? 'Rotate API key?' : 'Revoke API key?'}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirm?.type === 'rotate'
              ? `A new secret is issued for "${confirm?.key.name}". Anything using the old secret stops working until you update it.`
              : `"${confirm?.key.name}" stops working immediately. This cannot be undone.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)}>Cancel</Button>
          <Button
            color={confirm?.type === 'revoke' ? 'error' : 'primary'}
            variant="contained"
            disabled={confirmMutation.isPending}
            onClick={() => confirmMutation.mutate()}
          >
            {confirm?.type === 'rotate' ? 'Rotate' : 'Revoke'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default ApiKeysPage
